import checkOptionsFallback from './checkOptionsFallback';
import checkTargetLanguageValid from './checkTargetLanguageValid';
import getSupportedLanguages from '../services/getSupportedLanguages';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import loadOcsTranslationSettings from '../services/loadOcsTranslationSettings';
import mergeSettings from './mergeSettings';
import setSupportedLanguageMap from '../actions/setSupportedLanguageMap';
import setTranslationSettings from '../actions/setTranslationSettings';
import store from '../store/store';
import TranslationSettings from '../store/schema/TranslationSettings';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    OwsOptionsFeatureType,
    TranslationOptions,
    lazyGetServerOptionsForFeature,
} from 'owa-outlook-service-options';
import TranslationMode from '../store/schema/TranslationMode';

const initialRetryDelay: number = 15000;
let lastInitAttempt: number = 0;
let initRetryDelay: number = initialRetryDelay;

// This function initializes the translation store properties.
// It only updates the properties for the first call and
// any time user changes language (UserCulture in session settings)
// It returns 'true' when the store properties are updated.
export default async function initSupportedLanguages(): Promise<boolean> {
    const userCulture = getUserConfiguration().SessionSettings.UserCulture;
    const timeStamp = Date.now();

    // checks if feature flights are turned off, restore options to compatible values
    checkOptionsFallback();

    // this method is called on every message opened. Normally only the first time
    // it runs getSupportedLanguages as this is cached. In case it fails
    // initRetryDelay controls minimum time before we try again.
    if (
        (userCulture !== store.localeOfSupportedLanguageMap || !store.settings.initialized) &&
        (lastInitAttempt === 0 || timeStamp > lastInitAttempt + initRetryDelay)
    ) {
        let supportedLanguages = null;
        let correlationId = getGuid();
        const datapoint = new PerformanceDatapoint('InlineTranslationGetSupportedLanguages');
        try {
            if (store.supportedLanguageMap == null) {
                supportedLanguages = await getSupportedLanguages(userCulture, correlationId);
                setSupportedLanguageMap(userCulture, supportedLanguages);
                checkTargetLanguageValid();
            }
            if (!store.settings.initialized) {
                const getOptionsForFeature = await lazyGetServerOptionsForFeature.import();
                const options = await getOptionsForFeature<TranslationOptions>(
                    OwsOptionsFeatureType.Translation
                );
                const firstRun = isSettingMoveFirstRun(options);
                const ooSettings: TranslationSettings = {
                    translationMode: options.translationMode,
                    targetLanguage: options.targetLanguage != null ? options.targetLanguage : '',
                    excludedLanguages:
                        options.excludedLanguages == null ? [] : [...options.excludedLanguages],
                    initialized: false,
                };
                if (isFeatureEnabled('tra-useOcsSettings')) {
                    const ocsSettings = await loadOcsTranslationSettings();
                    // We only want merged setting - not waiting for the settings to be saved.
                    mergeSettings(ooSettings, ocsSettings, firstRun);
                }
                setTranslationSettings(ooSettings);
            }
            lastInitAttempt = 0;
            initRetryDelay = initialRetryDelay;
            datapoint.end();
        } catch (ex) {
            const error = new Error(
                '[InlineTranslation] Failed to initialize translation. Error:' +
                    ex +
                    ' | cid:' +
                    correlationId
            );

            // Double retry delay every attempt
            lastInitAttempt = timeStamp;
            initRetryDelay = 2 * initRetryDelay;
            datapoint.endWithError(DatapointStatus.ServerError, error);
            throw ex;
        }
        return true;
    }
    return false;
}

// Here we check if this is the first run after the flight is turned on for the user.
// If the translation mode value is within range, it is the first run after the flight
// has been turned on for the user. If not (value is higher than maximum possible value),
// we know that this is not the first run and the settings has been updated for the user
// in previous run by adding 0x10 to the value. So, we subtract 0x10 to get the actual
// mode. If for some reason, an invalid value is stored, we fallback to the default
// value of "SuggestAutomatic".
function isSettingMoveFirstRun(options: TranslationOptions) {
    if (options.translationMode > TranslationMode.Suggest) {
        options.translationMode = options.translationMode - 0x10;
        if (
            options.translationMode < TranslationMode.SuggestAutomatic ||
            options.translationMode > TranslationMode.Suggest
        ) {
            options.translationMode = TranslationMode.SuggestAutomatic;
        }
        return false;
    }
    return true;
}
